import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { isMfaChallenge, useLogin } from '@/lib/auth';
import { ApiError } from '@/lib/api';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

export function Login() {
  const navigate = useNavigate();
  // Set by ChangePassword after a password is chosen from an emailed link.
  const notice = (useLocation().state as { notice?: string } | null)?.notice;
  const login = useLogin();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: (response) => {
        if (isMfaChallenge(response)) {
          navigate('/mfa-verify', {
            replace: true,
            state: { mfaToken: response.mfaToken },
          });
          return;
        }
        navigate('/dashboard', { replace: true });
      },
    });
  });

  const errorMessage =
    login.error instanceof ApiError
      ? login.error.message
      : login.error
        ? 'Could not sign in. Try again.'
        : null;

  return (
    <div className="w-full max-w-md">
      <h1 className="font-display text-h1 font-bold">Welcome back</h1>
      <p className="mt-3 text-body text-text-secondary">
        Sign in to continue your compliance work.
      </p>

      {notice ? (
        <div className="mt-6 rounded-md border border-success/40 bg-success/5 px-4 py-3 text-body-sm text-success">
          {notice}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-gap-element-lg">
        <Field id="email" label="Email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            invalid={!!errors.email}
            {...register('email')}
          />
        </Field>

        <Field id="password" label="Password" error={errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            invalid={!!errors.password}
            {...register('password')}
          />
        </Field>

        {errorMessage ? (
          <div
            className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger"
            role="alert"
          >
            {errorMessage}
          </div>
        ) : null}

        <Button type="submit" size="lg" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>

        <div className="flex justify-between text-body-sm">
          <Link to="/reset" className="text-accent hover:underline">
            Forgot password?
          </Link>
          <Link to="/register" className="text-accent hover:underline">
            Create an account
          </Link>
        </div>
      </form>
    </div>
  );
}
